import { useEffect, useRef, useState } from 'react';
import {
  Modal,
  ModalBody,
  Button,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  InputGroup,
  InputGroupText,
  Row,
  Col,
} from 'reactstrap';
import AsyncCreatableSelect from 'react-select/async-creatable';
import FeatherIcon from 'feather-icons-react';
import { toast } from 'react-toastify';
import { searchPreMoveoutVendors, createPreMoveoutAssignment, updatePreMoveoutAssignment } from '../Helpers/Api';
import { isEmailLike, ASSIGNMENT_KIND, toDateInput } from '../Helpers/format';
import TimeSelect, { DEFAULT_TIME } from './TimeSelect';

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  control: (base, { isFocused }) => ({
    ...base,
    minHeight: 38,
    borderColor: isFocused ? '#0e578e' : '#e9e9ef',
    boxShadow: isFocused ? '0 0 0 0.15rem rgba(14, 87, 142, 0.25)' : 'none',
    '&:hover': { borderColor: isFocused ? '#0e578e' : '#ced4da' },
  }),
};

const toOption = (row) => ({
  value: `${row.source || 'vendor'}:${row.vendor_id || row.email}`,
  label: row.name ? `${row.name} (${row.email})` : row.email,
  data: row,
});

const formatOptionLabel = (opt) => {
  if (opt.__isNew__ || !opt.data) return <span>{opt.label}</span>;
  const { name, email, source, category } = opt.data;
  return (
    <div className="pm-vendor-opt">
      <div>
        <span className="pm-vendor-opt__name">{name || email}</span>
        {name && <span className="pm-vendor-opt__email">{email}</span>}
      </div>
      <span className="pm-kind pm-kind--source">
        {source === 'contractor' ? 'Contractor' : 'Vendor'}
        {category ? ` · ${category}` : ''}
      </span>
    </div>
  );
};

/** One line per repair: "Category — description" */
export const buildScopeFromReport = (report) =>
  (report?.repairs || [])
    .map((r) => `${r.category || 'Repair'}${r.description ? ` — ${r.description}` : ''}`)
    .join('\n');

/**
 * Assign a contractor or cleaner to a submitted report. POST /:id/assignments.
 */
const AssignVendorModal = ({ isOpen, onClose, report, assignment = null, onAssigned, zIndex = 1065 }) => {
  const [kind, setKind] = useState('contractor');
  const [vendorOption, setVendorOption] = useState(null);
  const [vendorName, setVendorName] = useState('');
  const [vendorEmail, setVendorEmail] = useState('');
  const [vendorId, setVendorId] = useState(null);
  const [scope, setScope] = useState('');
  const [estimate, setEstimate] = useState('');
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentTime, setAppointmentTime] = useState(DEFAULT_TIME);
  const [touched, setTouched] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setKind(assignment?.kind || 'contractor');
    setVendorOption(null);
    setVendorName(assignment?.vendor_name || '');
    setVendorEmail(assignment?.vendor_email || '');
    setVendorId(null);
    setScope(assignment?.scope || buildScopeFromReport(report));
    setEstimate(assignment?.estimate_price ?? '');
    setAppointmentDate(toDateInput(assignment?.appointment_date));
    setAppointmentTime(assignment?.appointment_time || DEFAULT_TIME);
    setTouched({});
    setBusy(false);
  }, [isOpen, report, assignment]);

  // Debounced async search; stale promises resolve empty and are ignored by react-select.
  const timerRef = useRef(null);
  const resolveRef = useRef(null);
  const loadOptions = (input) =>
    new Promise((resolve) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (resolveRef.current) resolveRef.current([]);
      resolveRef.current = resolve;
      timerRef.current = setTimeout(async () => {
        try {
          const rows = await searchPreMoveoutVendors(input || '');
          resolve((rows || []).map(toOption));
        } catch {
          resolve([]);
        } finally {
          if (resolveRef.current === resolve) resolveRef.current = null;
        }
      }, 250);
    });
  useEffect(() => () => timerRef.current && clearTimeout(timerRef.current), []);

  const handleVendorChange = (opt) => {
    setVendorOption(opt);
    if (!opt) {
      setVendorName('');
      setVendorEmail('');
      setVendorId(null);
      return;
    }
    if (opt.__isNew__) {
      setVendorEmail(String(opt.value || '').trim());
      setVendorId(null);
      return;
    }
    const row = opt.data || {};
    setVendorName(row.name || '');
    setVendorEmail(row.email || '');
    setVendorId(row.vendor_id || null);
  };

  const emailOk = isEmailLike(vendorEmail);
  const scopeOk = !!scope.trim();
  const estimateOk = estimate === '' || (!Number.isNaN(Number(estimate)) && Number(estimate) >= 0);
  const canSubmit = emailOk && scopeOk && estimateOk && appointmentDate && appointmentTime && !busy;

  const submit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, scope: true, estimate: true });
    if (!canSubmit) return;
    try {
      setBusy(true);
      const payload = {
        kind,
        appointment_date: appointmentDate,
        appointment_time: appointmentTime,
        vendor_email: vendorEmail.trim(),
        vendor_name: vendorName.trim(),
        scope: scope.trim(),
      };
      if (vendorId) payload.vendor_id = vendorId;
      if (estimate !== '') payload.estimate_price = Number(estimate);
      const doc = assignment ? await updatePreMoveoutAssignment(report._id, assignment._id, payload) : await createPreMoveoutAssignment(report._id, payload);
      if (doc.deliveryMessage?.includes('email') && (doc.deliveryMessage.includes('failed') || doc.deliveryMessage.includes('could not'))) toast.warning(doc.deliveryMessage);
      else toast.success(assignment ? 'Updated quote request sent' : `Quote request sent to ${payload.vendor_name || payload.vendor_email}`);
      if (onAssigned) onAssigned(doc);
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to assign the vendor');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={busy ? undefined : onClose} size="lg" centered scrollable zIndex={zIndex} className="pm-assign-modal">
      <form onSubmit={submit} noValidate>
        <div className="pm-modal-header">
          <div className="pm-header-copy">
            <h5>{assignment ? 'Edit quote request' : 'Assign vendor'}</h5>
            <div className="pm-modal-sub">
              Unit {report?.unit_number || '—'}
              {report?.property_details?.building_name ? ` · ${report.property_details.building_name}` : ''}
              {' · quote request email'}
            </div>
          </div>
          <button type="button" className="pm-icon-btn" onClick={onClose} aria-label="Close" disabled={busy}>
            <FeatherIcon icon="x" size={16} />
          </button>
        </div>

        <ModalBody>
          {assignment && <p className="alert alert-info">Changing the request clears the pending quote and asks the vendor for a fresh price.</p>}
          <div className="pm-workflow-note">Request a quote, review the cost, then confirm the appointment. The inspection locks once a quote is approved.</div>
          <Row>
            <Col md={6}><FormGroup><Label for="pm-appointment-date">Repair appointment date *</Label><Input id="pm-appointment-date" type="date" value={appointmentDate} onChange={e => setAppointmentDate(e.target.value)} disabled={busy} required /></FormGroup></Col>
            <Col md={6}><FormGroup><Label for="pm-appointment-time">Time (property local time) *</Label><TimeSelect inputId="pm-appointment-time" ariaLabel="Time (property local time) *" value={appointmentTime} onChange={setAppointmentTime} isDisabled={busy} /></FormGroup></Col>
          </Row>
          <FormGroup>
            <Label className="form-label d-block">Kind</Label>
            <div className="btn-group" role="group" aria-label="Vendor kind">
              {Object.keys(ASSIGNMENT_KIND).map((k) => (
                <Button
                  key={k}
                  type="button"
                  color={kind === k ? 'primary' : 'light'}
                  aria-pressed={kind === k}
                  onClick={() => setKind(k)}
                  disabled={busy || !!assignment}
                  style={{ minWidth: 120, minHeight: 38 }}
                >
                  <FeatherIcon icon={k === 'contractor' ? 'tool' : 'wind'} size={15} className="me-1" />
                  {ASSIGNMENT_KIND[k]}
                </Button>
              ))}
            </div>
          </FormGroup>

          <FormGroup>
            <Label for="pm-av-vendor" className="form-label">
              Vendor
            </Label>
            <AsyncCreatableSelect
              inputId="pm-av-vendor"
              value={vendorOption}
              onChange={handleVendorChange}
              loadOptions={loadOptions}
              defaultOptions
              cacheOptions
              isClearable
              isDisabled={busy || !!assignment}
              placeholder="Search vendors and contractors by name or email…"
              noOptionsMessage={({ inputValue }) =>
                inputValue ? 'No match — type a full email address to add a new vendor' : 'Type to search'
              }
              formatOptionLabel={formatOptionLabel}
              isValidNewOption={(input) => isEmailLike(input)}
              formatCreateLabel={(input) => `Use "${input}" as the vendor email`}
              getNewOptionData={(input, label) => ({ value: input, label, __isNew__: true })}
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              className="react-select-container"
              classNamePrefix="react-select"
            />
            <div className="form-text">Pick a saved vendor or contractor, or type a new email address.</div>
          </FormGroup>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="pm-av-name" className="form-label">
                  Vendor name
                </Label>
                <Input
                  id="pm-av-name"
                  type="text"
                  readOnly={!!assignment}
                  value={vendorName}
                  onChange={(e) => setVendorName(e.target.value)}
                  placeholder="Company or contact name"
                  disabled={busy}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="pm-av-email" className="form-label">
                  Vendor email <span className="text-danger">*</span>
                </Label>
                <Input
                  id="pm-av-email"
                  type="email"
                  readOnly={!!assignment}
                  value={vendorEmail}
                  onChange={(e) => setVendorEmail(e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                  invalid={touched.email && !emailOk}
                  placeholder="vendor@example.com"
                  disabled={busy}
                  required
                />
                <FormFeedback>A valid email address is required</FormFeedback>
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label for="pm-av-scope" className="form-label">
              What needs to be done <span className="text-danger">*</span>
            </Label>
            <Input
              id="pm-av-scope"
              type="textarea"
              rows={6}
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, scope: true }))}
              invalid={touched.scope && !scopeOk}
              placeholder="One line per item, e.g. Painting — bedroom walls scuffed"
              disabled={busy}
              required
            />
            <FormFeedback>Describe the scope of work</FormFeedback>
            <div className="form-text">Prefilled from the report's repairs; edit freely before sending.</div>
          </FormGroup>

          <Row>
            <Col md={5}>
              <FormGroup className="mb-0">
                <Label for="pm-av-estimate" className="form-label">
                  Internal estimate (CAD)
                </Label>
                <InputGroup>
                  <InputGroupText>$</InputGroupText>
                  <Input
                    id="pm-av-estimate"
                    type="number"
                    min="0"
                    step="0.01"
                    value={estimate}
                    onChange={(e) => setEstimate(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, estimate: true }))}
                    invalid={touched.estimate && !estimateOk}
                    placeholder="Optional"
                    disabled={busy}
                  />
                  <FormFeedback>Enter a positive amount</FormFeedback>
                </InputGroup>
                <div className="form-text">Not shown to the vendor.</div>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>

        <div className="pm-modal-footer justify-content-end">
          <Button type="button" color="light" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" color="primary" disabled={!canSubmit}>
            <FeatherIcon icon="send" size={15} className="me-1" />
            {busy ? 'Sending…' : 'Send quote request'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export { AssignVendorModal };
export default AssignVendorModal;
